import { Router } from "@frui.ts/screens";
import ApprovalListViewModel from "../../../common/approvalListViewModel";
import type DistributorRefundResponse from "entities/distributorRefundResponse";
import DistributorRefundDetailViewModel from "./distributorRefundDetailViewModel";
import DistributorRefundsSearchFilter, {
  DistributorRefundsSearchFilterValidationRules,
} from "entities/filters/refunds/distributorRefundsSearchFilter";
import LocalizationService from "services/localizationService";
import EventBus, { distributorRefundGenerated } from "services/eventBus";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import { bound } from "@frui.ts/helpers";
import { Modules } from "config";
import RefundType from "entities/refundType";
import RefundRepository from "repositories/refundRepository";
import OptionsService from "services/optionsService";
import { computed, reaction } from "mobx";
import { attachAutomaticValidator } from "@frui.ts/validation";
import DistributorPaymentRequestType from "entities/distributorPaymentRequestType";
import RefundApprovalType from "entities/refundApprovalType";
import RefundsApprovalService from "services/refundsApprovalService";
import type DistributorPaymentRequestResponse from "entities/distributorPaymentRequestResponse";
import type { IRefund } from "entities/types";
import Selectable, { SelectableListViewModel } from "../../../selectableListViewModel";

const navigationName = "distributors";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [DistributorRefundDetailViewModel],
})
@Selectable
export default class DistributorRefundsViewModel extends ApprovalListViewModel<
  RefundApprovalType,
  DistributorRefundResponse,
  DistributorRefundDetailViewModel,
  DistributorRefundsSearchFilter
> {
  readonly modelName = "distributorRefundResponse";
  readonly refundType = RefundType.Distributor;
  readonly approvalType = RefundApprovalType;

  @computed
  get requestTypeOptions() {
    return this.optionsService.internalEnumOptions(DistributorPaymentRequestType);
  }

  @computed
  get self(): SelectableListViewModel<DistributorRefundsViewModel, DistributorRefundResponse> {
    return this as any;
  }

  protected processData(items: DistributorRefundResponse[]) {
    items.forEach(item => {
      item.isSelectable = (this.canApprove(item) || false) && this.myApprovalStep(item);

      return item;
    });

    return items;
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof DistributorRefundDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected repository: RefundRepository,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
    this.navigationName = navigationName;
  }

  @bound
  onItemClick() {
    return (item: DistributorRefundResponse) => this.tryActivateChild(this.detailViewModelFactory(item.id, this.pagingFilter));
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.distributorName,
          this.filter.distributorsRequestVariableSymbol,
          this.filter.distributorsRequestType,
          this.filter.amount,
          this.filter.variableSymbol,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      ),
      this.eventBus.subscribe(distributorRefundGenerated, this.onDistributorRefundGenerated)
    );
    return super.onInitialize();
  }

  protected createFilter() {
    return attachAutomaticValidator(new DistributorRefundsSearchFilter(), DistributorRefundsSearchFilterValidationRules);
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    this.repository
      .getAllDistributorRefunds({ limit: 1, offset: 0 }, { approvedBy: RefundApprovalType.ChiefAccountant })
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.getAllDistributorRefunds({ offset, limit }, this.appliedFilter);
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName && this.allowWrite) {
      return this.detailViewModelFactory(navigationName, this.pagingFilter);
    }

    return Promise.resolve(undefined);
  }

  @bound
  onDistributorRefundGenerated(event: ReturnType<typeof distributorRefundGenerated>) {
    this.loadData();
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.distributor.${code}`, size);
  }

  translateRequestType(requestType: DistributorPaymentRequestType) {
    return this.localizationService.translateEnum("distributorPaymentRequestType", requestType);
  }

  translateModelAttribute(model: "distributorPaymentRequest", attribute: keyof DistributorPaymentRequestResponse): string;
  translateModelAttribute(model: "refund", attribute: keyof IRefund): string;
  translateModelAttribute(
    model: "distributorPaymentRequest" | "refund",
    attribute: keyof DistributorPaymentRequestResponse | keyof IRefund
  ) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  @bound
  translateModuleGeneral(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.${code}`, size);
  }
}
